// Funciones puras de mapeo de Order Batches — separadas del fetcher
// para que sean fácilmente testeables sin levantar fetch mocks.
//
// Tres responsabilidades:
//   1. adaptOrderBatch: DTO crudo del backend → tipo OrderBatch interno
//      (defaults para campos opcionales).
//   2. saleRowsToCsvRows: SaleRow[] del parser CSV → payload del endpoint
//      /api/order-batches/csv (renombre camelCase → snake_case, lat/lon
//      NaN → null).
//   3. batchOrderRowsToSaleRows: BatchOrderRow[] del backend → SaleRow[]
//      consumido por el optimizador. Filtra rows sin coords válidas y
//      reasigna `id` 0..N para mantener el invariante del optimizador.

import type {
  BatchOrderRow,
  OrderBatch,
  OrderBatchStatus,
  UploadOrderBatchCsvReq,
} from "../../data/orderBatchTypes";
import type { SaleRow } from "./types";

export function adaptOrderBatch(raw: Record<string, unknown>): OrderBatch {
  return {
    id: raw.id as string,
    name: (raw.name as string) ?? "",
    source: (raw.source as OrderBatch["source"]) ?? "csv",
    status: (raw.status as OrderBatchStatus) ?? "created",
    warehouse_id: (raw.warehouse_id as string | undefined) ?? undefined,
    num_orders: (raw.num_orders as number) ?? 0,
    notes: (raw.notes as string | undefined) ?? undefined,
    created_at: (raw.created_at as string) ?? "",
  };
}

export function saleRowsToCsvRows(rows: SaleRow[]): UploadOrderBatchCsvReq["rows"] {
  return rows.map((r) => ({
    cod_cliente: (r.codCliente ?? "").toString(),
    cliente: r.cliente ?? "",
    lat: Number.isFinite(r.lat) ? r.lat : null,
    lon: Number.isFinite(r.lon) ? r.lon : null,
    valor_venta: Number.isFinite(r.valorVenta) ? r.valorVenta : 0,
    transportista: r.transportista ?? "",
    peso_kg: r.pesoKg,
    volumen_m3: r.volumenM3,
  }));
}

export type BatchRowMappingResult = {
  rows: SaleRow[];
  skipped: number;
};

export function batchOrderRowsToSaleRows(dbRows: BatchOrderRow[]): BatchRowMappingResult {
  const rows: SaleRow[] = [];
  let skipped = 0;
  for (const r of dbRows) {
    if (
      r.lat == null ||
      r.lon == null ||
      !Number.isFinite(r.lat) ||
      !Number.isFinite(r.lon)
    ) {
      skipped++;
      continue;
    }
    rows.push({
      id: rows.length,
      transportista: r.transportista || "",
      cliente: r.cliente,
      codCliente: r.cod_cliente,
      lat: r.lat,
      lon: r.lon,
      valorVenta: r.valor_venta,
      pesoKg: r.peso_kg,
      volumenM3: r.volumen_m3,
    });
  }
  return { rows, skipped };
}
